import React, { FC, useContext, useEffect, useState } from 'react';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import styles from './organisation.module.scss';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import {
  FormValues,
  ReportValues,
  transformToPayload,
} from './companySetupConstants';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import {
  CompanySetUpTabHash,
  CompanySetupEnum,
  LocationTableFields,
  pageSize,
} from 'enums/CompanySetupEnums';
import CancelFormModal from 'shared/components/CancelFormModal';
import LocationModal from './ParentPage/Modals/locationModal';
import OrganizationDetailsForm from './ParentPage/Forms/OrganizationDetailsForm';
import ReportingDetailsForm from './ParentPage/Forms/reportingForm';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import COPagination from 'shared/components/COPagination';
import useFilters from 'shared/hooks/useFilters';
import LocationListing from './ParentPage/LocationList/locationListing';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import { OrganizationModalContent } from './ParentPage/Modals/OrganisationModalContent';
import { EmptyStateWrapper } from './ParentPage/EmptyStateWrapper';
import { CompanySetupHeader } from './ParentPage/CompanySetupHeader';
import TimelineModal from './ParentPage/Timeline/Timeline';
import { SortingTypesEnums } from 'enums/sortingTypes.enum';
import { pageCountOne } from 'shared/components/COProductCard/constants';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import DemoVideoService from 'services/DemoVideoService/demoVideo.service';
import { DemoVideoTypes } from 'enums/demoVideoEnums';
import VideoModal from 'shared/components/DemoVideoModal/VideoModal';
import MetadataService from 'services/ParentCompanySetup/metadata.service';

const CompanySetup: FC = () => {
  const { getHash, setHash } = useFilters();
  const currentSearchParams = new URLSearchParams(window.location.search);

  const {
    fetchParentOrganization,
    createParentOrganization,
    fetchAllLocations,
    loading,
  } = ParentCompanySetupService();
  const { fetchYearsMetaData, yearsMetaData } = MetadataService();

  const { organizationData } = useParentOrganizationContext();
  const {
    fetchDemoWatchStatus,
    demoWatchedStatus,
    fetchDemoVideoLink,
    demoVideos,
    updateDemoWatchStatus,
  } = DemoVideoService();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { locationTableData, setLocationTableData } = useLocationsDataContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [payloadValues, setPayloadValues] = useState<any>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isOrgVisible, setIsOrgVisible] = useState(false);
  const [isTimeLineVisible, setIsTimeLineVisible] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [DeleteModalState, setDeleteModalState] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    CompanySetupEnum.ORG_DETAILS
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [activeChildTab, setActiveChildTab] = useState<string>(
    CompanySetupEnum.LOCATIONS
  );
  const [page, setPage] = useState<number>(
    Number(searchParams.get('page')) || 1
  );
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const toggleVideoDisplay = () => setPlayVideo(prev => !prev);
  const searchText = searchParams.get('search') || '';

  useEffect(() => {
    !getHash() && setHash(CompanySetUpTabHash.LOCATIONS);
    fetchYearsMetaData();
    fetchParentOrganization();
    fetchDemoWatchStatus();
    return () => {
      //Clearing location context when the component is unmounted so that older data won't be visible on updates and route changes
      setLocationTableData(null);
    };
  }, []);

  // Demo video implementation
  useEffect(() => {
    if (demoWatchedStatus && !demoWatchedStatus?.ocfStatus) {
      fetchDemoVideoLink();
      toggleVideoDisplay();
    } else {
      updateDemoWatchStatus(DemoVideoTypes.OCF);
    }
  }, [demoWatchedStatus]);

  useEffect(() => {
    const baseLineYear = organizationData?.company?.baselineYear;
    if (baseLineYear && yearsMetaData?.length) {
      const locationsInBaseLineYear = yearsMetaData?.find(
        year => year.value == baseLineYear
      );
      const defaultYear: number = locationsInBaseLineYear
        ? baseLineYear
        : Number(yearsMetaData[0].value);
      setSelectedYear(defaultYear);
    }
  }, [organizationData, yearsMetaData]);

  const handleOrgModal = () => {
    setIsTimeLineVisible(false);
    setIsOrgVisible(true);
  };
  const handleCancelModal = () => setDeleteModalState(true);
  const handleTimeLineModal = () => setIsTimeLineVisible(true);
  const handleSubmit = (values: FormValues) => {
    const payloadData = transformToPayload(values);
    setPayloadValues(payloadData);
    setActiveTab(CompanySetupEnum.REPORTING_DETAILS);
  };
  const handleReportingSubmit = async (values: ReportValues) => {
    const updatePayload = Object.assign({}, payloadValues, {
      data: Object.assign({}, payloadValues.data, {
        company: Object.assign({}, payloadValues.data.company, {
          reporting_month: values.startOfReportingYear,
          baseline_year: values.baselineYear,
          recalculation_threshold: values.recalculationThreshold || 0,
        }),
      }),
    });

    await createParentOrganization(updatePayload);
    setIsOrgVisible(false);
    setLocationModal(true);
    fetchParentOrganization();
  };
  const sortby = currentSearchParams.get('sort_by') || '';
  const sortDir = currentSearchParams.get('sort_dir') || '';

  useEffect(() => {
    const currPage = Number(currentSearchParams.get('page')) || page;
    const debouncedFetch = debounce(() => {
      fetchAllLocations({
        year: selectedYear || organizationData?.company?.baselineYear,
        page: currPage,
        search: searchText,
        sort_by: sortby,
        sort_dir:
          sortDir === SortingTypesEnums.ASCEND
            ? LocationTableFields.ASC
            : sortDir === SortingTypesEnums.DESCEND
            ? LocationTableFields.DESC
            : '',
      });
    }, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [
    searchText,
    sortby,
    selectedYear,
    sortDir,
    page,
    organizationData?.company?.baselineYear,
  ]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    currentSearchParams.set('search', value);
    currentSearchParams.set('page', pageCountOne.toString());
    setSearchParams(currentSearchParams);
  };
  const handleTabChangeInChild = (tab: string) => setActiveChildTab(tab);
  const handleBackToOrgDetails = () =>
    setActiveTab(CompanySetupEnum.ORG_DETAILS);

  const handleCloseDeleteModal = () => setDeleteModalState(false);

  const handleModalConfirm = () => {
    setActiveTab(CompanySetupEnum.ORG_DETAILS);
    setIsOrgVisible(false);
    setIsTimeLineVisible(false);
    setDeleteModalState(false);
    setLocationModal(false);
  };

  const handleModalCancel = () => handleCloseDeleteModal();

  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const tabs = [
    {
      key: CompanySetupEnum.ORG_DETAILS,
      label: CompanySetupEnum.ORG,
      children: (
        <OrganizationDetailsForm
          handleSubmit={handleSubmit}
          handleCancelModal={handleCancelModal}
          handleToolTipOpen={handleToolTipOpen}
        />
      ),
    },
    {
      key: CompanySetupEnum.REPORTING_DETAILS,
      label: CompanySetupEnum.REPORTING,
      children: (
        <ReportingDetailsForm
          handleSubmit={handleReportingSubmit}
          handleBackToOrgDetails={handleBackToOrgDetails}
          handleToolTipOpen={handleToolTipOpen}
        />
      ),
    },
  ];
  const inputValue = searchParams.get('search') || searchValue;

  const handlePageChange = (pageNum: number) => {
    currentSearchParams.set('page', pageNum.toString());
    setSearchParams(currentSearchParams);
    setPage(pageNum);
  };
  const isPaginationVisible =
    (getHash() === '' || getHash() === CompanySetupEnum.LOCATION_Text) &&
    !loading &&
    locationTableData;

  const demoVideoUrl = demoVideos?.find(
    item => item.type === DemoVideoTypes.OCF
  )?.url;

  return (
    <DashboardContainer>
      <CompanySetupHeader
        activeChildTab={activeChildTab}
        inputValue={inputValue}
        handleInputChange={handleInputChange}
      />

      <div>
        {organizationData?.organization && (
          <LocationListing
            selectedYear={selectedYear}
            onTabChange={handleTabChangeInChild}
            yearsMetaData={yearsMetaData}
            setSelectedYear={setSelectedYear}
            tableLoading={loading}
          />
        )}
        {isPaginationVisible && (
          <div className={styles.paginationContainer}>
            <COPagination
              onChange={handlePageChange}
              total={locationTableData?.totalItems || pageSize}
              pageSize={pageSize}
              current={locationTableData?.currentPage}
            />
          </div>
        )}
      </div>

      <CancelFormModal
        handleModalCancel={handleModalCancel}
        handleModalConfirm={handleModalConfirm}
        showModalState={DeleteModalState}
        heading={CompanySetupEnum.CloseFormHeading}
        subHeading={CompanySetupEnum.CloseFormSubHeading}
      />
      {locationModal && (
        <LocationModal
          showModal={locationModal}
          onCancel={handleCancelModal}
          handleToolTipOpen={handleToolTipOpen}
        />
      )}
      <TimelineModal
        isVisible={isTimeLineVisible}
        onProceed={handleOrgModal}
        onClose={handleModalConfirm}
      />
      <OrganizationModalContent
        isOrgVisible={isOrgVisible}
        handleCancelModal={handleCancelModal}
        tabs={tabs}
        activeTab={activeTab}
      />
      <EmptyStateWrapper
        loading={loading}
        handleOrgModal={handleTimeLineModal}
        setLocationModal={setLocationModal}
      />
      {playVideo && demoVideoUrl && (
        <VideoModal
          isOpen={playVideo}
          videoUrl={demoVideoUrl}
          onClose={toggleVideoDisplay}
        />
      )}
    </DashboardContainer>
  );
};

export default CompanySetup;
